import React, { useEffect, useRef, useState } from 'react';
import api from '@utils/api';
import '@moncler/scss/components/cube-gallery/cube-gallery.scss';
import gsap from 'gsap';
import ScrollTrigger from 'gsap/ScrollTrigger';
gsap.registerPlugin(ScrollTrigger);
import { debounce } from '@common/utils/functions';
import cookie from 'react-cookies';
import CubeGallerySlide from './CubeGallerySlide';
const markers = cookie.load('gsap-markers') === 'cube-genius';
const CubeGalleryGenius = (props) => {
    const [componentData, setComponentData] = useState(null);
    const [slides, setSlides] = useState([]);
    const [dragPosition, setDragPosition] = useState(0);
    const [isReady, setIsReady] = useState(false);
    const [isDragging, setIsDragging] = useState(false);
    const [currentSlide, setCurrentSlide] = useState(0);
    const interactionIsLocked = useRef(false);
    const ref = useRef(null);
    const nextButtonRef = useRef(null);
    const prevButtonRef = useRef(null);
    const slideIndex = useRef(0);
    const slideLength = useRef(0);
    const dotsRef = useRef(null);
    const isMoving = useRef(false);
    const dragThreshold = 50; // Arbitary
    useEffect(() => {
        if (componentData) {
            const observer = new IntersectionObserver(debounce(() => {
                var _a;
                (_a = ScrollTrigger.getById('cube-gallery-genius')) === null || _a === void 0 ? void 0 : _a.refresh();
            }, 1000));
            const body = document.querySelector('body');
            observer.observe(body);
            return () => {
                observer.unobserve(body);
            };
        }
    }, [componentData]);
    useEffect(() => {
        var _a;
        let pageID = (_a = document.body.id) === null || _a === void 0 ? void 0 : _a.replace('page-', '');
        if (pageID) {
            api.getPageJson(pageID, true).then((data) => {
                var _a;
                let componentDataRes = (_a = data.regions[0].components) === null || _a === void 0 ? void 0 : _a.find((c) => c.id === props.uuid);
                let componentDataMerged = Object.assign(Object.assign({}, componentDataRes.data), { custom: componentDataRes.custom });
                setComponentData(componentDataMerged);
                let slidesData = componentDataRes.regions[0].components;
                setSlides(slidesData);
            });
        }
    }, []);
    useEffect(() => {
        var _a;
        if (componentData && ref.current) {
            let slides = ref.current.querySelectorAll('.slide');
            const slidesArray = gsap.utils.toArray(slides);
            slides.forEach((slide, index) => {
                if (index === 0) {
                    setNextActive(slide, 0, true);
                }
                else {
                    setNextInactive(slide);
                }
            });
            slideLength.current = slides.length;
            const tl = gsap.timeline({ paused: true, defaults: { duration: 1.0, ease: 'ease-in' } });
            // set the correct size of the slider and slides - haldle the sesize of the window
            gsap.set(ref.current, { perspective: ref.current.offsetWidth * 1.5, scale: 0.6 });
            gsap.set(slidesArray, {
                backfaceVisibility: 'hidden',
                transformOrigin: '50% 50% -' + ref.current.offsetWidth / 2,
            });
            window.addEventListener('resize', function () {
                gsap.set(ref.current, { perspective: ref.current.offsetWidth * 1.5 });
                gsap.set(slidesArray, {
                    transformOrigin: '50% 50% -' + ref.current.offsetWidth / 2,
                });
            });
            gsap.set(ref.current.querySelector('.slide:nth-child(1)'), { rotationY: -45 });
            gsap.set(ref.current.querySelector('.slide:nth-child(2)'), { rotationY: 45, autoAlpha: 1 });
            tl.to(ref.current, { scale: 1 })
                .to(ref.current.querySelector('.slide:nth-child(1)'), { rotationY: 0 }, '>')
                .to(ref.current.querySelector('.slide:nth-child(2)'), {
                rotationY: 90,
                onStart: () => {
                    ref.current.querySelector('.slide:nth-child(1)').classList.add('active');
                },
                onComplete: () => {
                    setIsReady(true);
                },
            }, '<');
            ScrollTrigger.create({
                trigger: (_a = ref.current) === null || _a === void 0 ? void 0 : _a.parentNode,
                start: () => `top center`,
                markers: markers,
                onEnter: () => {
                    tl.play();
                },
                id: 'cube-gallery-genius',
            });
            prevButtonRef.current.addEventListener('click', function () {
                var _a;
                if (interactionIsLocked.current) {
                    return;
                }
                interactionIsLocked.current = true;
                (_a = ref.current.querySelector('.slide.active')) === null || _a === void 0 ? void 0 : _a.classList.remove('active');
                setTimeout(() => {
                    setSlide(slides[slideIndex.current], slides[slideIndex.current - 1] || slides[slides.length - 1], 'prev');
                }, 300);
            });
            nextButtonRef.current.addEventListener('click', function () {
                var _a;
                if (interactionIsLocked.current) {
                    return;
                }
                interactionIsLocked.current = true;
                (_a = ref.current.querySelector('.slide.active')) === null || _a === void 0 ? void 0 : _a.classList.remove('active');
                setTimeout(() => {
                    setSlide(slides[slideIndex.current], slides[slideIndex.current + 1] || slides[0], 'next');
                }, 300);
            });
        }
    }, [slides]);
    const setSlide = (currentSlide, nextSlide, direction, jump) => {
        if (nextSlide === currentSlide) {
            return;
        }
        if (direction === 'next') {
            setNextInactive(currentSlide, 1.2);
            setNextActive(nextSlide, 1.2);
            if (jump || jump === 0) {
                slideIndex.current = jump;
                setCurrentSlide(jump);
            }
            else {
                slideIndex.current = slideIndex.current === slideLength.current - 1 ? 0 : slideIndex.current + 1;
                setCurrentSlide(slideIndex.current);
            }
        }
        if (direction === 'prev') {
            setPrevInactive(currentSlide, 1.2);
            setPrevActive(nextSlide, 1.2);
            if (jump || jump === 0) {
                slideIndex.current = jump;
                setCurrentSlide(jump);
            }
            else {
                slideIndex.current = slideIndex.current === 0 ? slideLength.current - 1 : slideIndex.current - 1;
                setCurrentSlide(slideIndex.current);
            }
        }
        let dots = dotsRef.current.querySelectorAll('.dot');
        dots.forEach((dot, index) => {
            dot.classList.remove('active');
            if (index === slideIndex.current) {
                dot.classList.add('active');
                setCurrentSlide(index);
            }
        });
        setTimeout(() => {
            interactionIsLocked.current = false;
        }, 1200);
    };
    const setNextActive = (slide, duration, first) => {
        if (!first) {
            slide.classList.add('active');
        }
        gsap.fromTo(slide, duration || 0, { rotationY: 90, ease: 'ease-in-out', autoAlpha: 1 }, { xPercent: 0, rotationY: 0 });
    };
    const setNextInactive = (slide, duration) => {
        slide.classList.remove('active');
        gsap.to(slide, duration || 0, { rotationY: -90, ease: 'ease-in-out', onComplete: () => gsap.set(slide, { autoAlpha: 0 }) });
    };
    const setPrevActive = (slide, duration) => {
        slide.classList.add('active');
        gsap.fromTo(slide, duration || 0, { rotationY: -90, ease: 'ease-in-out', autoAlpha: 1 }, { rotationY: 0 });
    };
    const setPrevInactive = (slide, duration) => {
        slide.classList.remove('active');
        gsap.to(slide, duration || 0, {
            rotationY: 90,
            onComplete: () => gsap.set(slide, { autoAlpha: 0, ease: 'ease-in-out' }),
        });
    };
    if (!componentData) {
        return React.createElement(React.Fragment, null);
    }
    let img = new Image();
    img.style.display = 'none';
    img.src = 'data:image/gif;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAQAAAC1HAwCAAAAC0lEQVR42mNkYAAAAAYAAjCB0C8AAAAASUVORK5CYII=';
    return (React.createElement("div", { className: "cube-gallery-module", style: { pointerEvents: isReady ? 'auto' : 'none' } },
        React.createElement("div", { className: "cube-gallery", ref: ref, role: "presentation", onMouseDown: (e) => {
                if (isReady) {
                    setDragPosition(e.screenX);
                    // gsap.set(ref.current, { transition: 'all 0.8s ease-in-out' });
                    // ref.current.querySelector('.slide.active')?.classList.remove('active');
                }
            }, onMouseMove: (e) => {
                if (isReady) {
                    setIsDragging(dragPosition && Math.abs(e.screenX - dragPosition) > dragThreshold);
                }
            }, onMouseUp: (e) => {
                if (isReady && isDragging) {
                    e.preventDefault();
                    e.stopPropagation();
                    setIsReady(false);
                    gsap.set(ref.current, { scale: 1 });
                    if (e.screenX < dragPosition) {
                        setSlide(ref.current.querySelectorAll('.slide')[slideIndex.current], ref.current.querySelectorAll('.slide')[slideIndex.current + 1] || ref.current.querySelectorAll('.slide')[0], 'next');
                    }
                    else {
                        setSlide(ref.current.querySelectorAll('.slide')[slideIndex.current], ref.current.querySelectorAll('.slide')[slideIndex.current - 1] || ref.current.querySelectorAll('.slide')[slideLength.current - 1], 'prev');
                    }
                }
                setDragPosition(null);
                setIsDragging(false);
                setTimeout(() => {
                    setIsReady(true);
                }, 1200);
            }, onTouchMove: (e) => {
                if (interactionIsLocked.current) {
                    return;
                }
                if (!isMoving.current) {
                    if (e.target.tagName === 'BUTTON') {
                        setDragPosition(null);
                        return;
                    }
                    if (isReady) {
                        gsap.set(ref.current, { scale: 0.98, transition: 'all 0.7s ease-in-out' });
                        // ref.current.querySelector('.slide.active')?.classList.remove('active');
                        setDragPosition(e.touches[0].screenX);
                        isMoving.current = true;
                    }
                }
            }, onTouchEnd: (e) => {
                var _a;
                if (interactionIsLocked.current) {
                    return;
                }
                if (e.target.tagName === 'BUTTON') {
                    return;
                }
                if (isReady && dragPosition && isMoving.current) {
                    if (Math.abs(e.changedTouches[0].screenX - dragPosition) > dragThreshold) {
                        (_a = ref.current.querySelector('.slide.active')) === null || _a === void 0 ? void 0 : _a.classList.remove('active');
                    }
                    gsap.set(ref.current, { scale: 1 });
                    setTimeout(() => {
                        // move only if the drag is more than 20px
                        if (Math.abs(e.changedTouches[0].screenX - dragPosition) > dragThreshold) {
                            if (e.changedTouches[0].screenX < dragPosition) {
                                setSlide(ref.current.querySelectorAll('.slide')[slideIndex.current], ref.current.querySelectorAll('.slide')[slideIndex.current + 1] || ref.current.querySelectorAll('.slide')[0], 'next');
                            }
                            else {
                                setSlide(ref.current.querySelectorAll('.slide')[slideIndex.current], ref.current.querySelectorAll('.slide')[slideIndex.current - 1] || ref.current.querySelectorAll('.slide')[slideLength.current - 1], 'prev');
                            }
                        }
                        else {
                            ref.current.querySelectorAll('.slide')[slideIndex.current].classList.add('active');
                        }
                        isMoving.current = false;
                    }, 300);
                }
            } }, slides.map((slide, index) => (React.createElement(CubeGallerySlide, { key: index, slide: slide, slideIndex: currentSlide, enableClick: !isDragging })))),
        React.createElement("div", { className: "controls" },
            React.createElement("button", { className: "prev-button", ref: prevButtonRef },
                React.createElement("span", { className: "icon--arrow-left" })),
            React.createElement("div", { className: "dots", ref: dotsRef }, slides.map((dot, index) => (
            // eslint-disable-next-line jsx-a11y/click-events-have-key-events
            React.createElement("div", { role: "button", key: index, className: `dot ${index === slideIndex.current ? 'active' : ''}`, tabIndex: 0, onClick: () => {
                    var _a;
                    if (interactionIsLocked.current) {
                        return;
                    }
                    interactionIsLocked.current = true;
                    // remove active class from all slides
                    (_a = ref.current.querySelector('.slide.active')) === null || _a === void 0 ? void 0 : _a.classList.remove('active');
                    setTimeout(() => {
                        setSlide(ref.current.querySelectorAll('.slide')[slideIndex.current], ref.current.querySelectorAll('.slide')[index], index > slideIndex.current ? 'next' : 'prev', index);
                    }, 300);
                } },
                React.createElement("div", { className: "dot-line" }))))),
            React.createElement("button", { className: "next-button", ref: nextButtonRef },
                React.createElement("span", { className: "icon--arrow-right" })))));
};
export default CubeGalleryGenius;
